import React, { useId, useState } from 'react';
import { useIntl, defineMessage } from 'react-intl';
import { UncontrolledTooltip } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHeart } from '@fortawesome/pro-light-svg-icons/faHeart';
import { TypedMemo } from '../../../common/types';
import { useTRPCAPI, useTRPCQuery } from '../../../../trpc/trpc';
import { useFollowArtist } from '../../hooks/useFollowArtist';
import { useFollowTrack } from '../../hooks/useFollowTrack';
import { useShareTokenContext } from '../../contexts/ShareTokenContext';
import { useUserToken } from '../../../../redux/slices/appInfo/hooks';
import { isAnonymousShareView } from './isAnonymousShareView';
/* eslint-disable css-modules/no-unused-class */
import styles from './FollowButton.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface FollowButtonProps {
  artistId?: string;
  trackId?: string;
  variant?: 'button' | 'icon';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onFollowChange?: (isFollowing: boolean) => void;
}

const messages = {
  follow: defineMessage({
    id: 'app.sml.followButton.follow',
    defaultMessage: 'Follow',
  }),
  following: defineMessage({
    id: 'app.sml.followButton.following',
    defaultMessage: 'Following',
  }),
  unfollow: defineMessage({
    id: 'app.sml.followButton.unfollow',
    defaultMessage: 'Unfollow',
  }),
  publicTooltip: defineMessage({
    id: 'app.sml.publicMode.followButton.tooltip',
    defaultMessage: 'Sign up to follow',
  }),
};

const FollowButtonComponent: React.FC<FollowButtonProps> = ({
  artistId,
  trackId,
  variant = 'button',
  size = 'md',
  className = '',
  onFollowChange,
}) => {
  const intl = useIntl();
  const trpcMusicAPI = useTRPCAPI();
  const [isHovered, setIsHovered] = useState(false);
  const [optimisticFollowState, setOptimisticFollowState] = useState<boolean | null>(null);
  const { isPublicMode } = useShareTokenContext();
  const userToken = useUserToken();
  const isAnonymousShare = isAnonymousShareView(isPublicMode, userToken);
  const generatedId = useId();
  const tooltipTargetId = `follow-btn-${generatedId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  // Validation: exactly one of artistId or trackId must be provided
  if (!artistId && !trackId) {
    throw new Error('FollowButton: Either artistId or trackId must be provided');
  }
  if (artistId && trackId) {
    throw new Error('FollowButton: Only one of artistId or trackId should be provided');
  }

  const isArtist = Boolean(artistId);
  const entityId = artistId || trackId || '';

  // Query to check if user is following the entity.
  // In public (share-token) mode this endpoint is protected, so skip the query entirely.
  const { data: followStatus, refetch: refetchFollowStatus } = useTRPCQuery(
    isArtist
      ? trpcMusicAPI.artists.isFollowing.queryOptions({ artistId: entityId }, { enabled: !isAnonymousShare })
      : trpcMusicAPI.tracks.isFollowing.queryOptions({ trackId: entityId }, { enabled: !isAnonymousShare })
  );

  // Use optimistic state if available, otherwise fall back to server state
  const isFollowing = optimisticFollowState ?? followStatus?.isFollowing ?? false;

  // Use the reusable follow hooks
  const {
    followArtist,
    unfollowArtist,
    isPending: isArtistPending,
  } = useFollowArtist({
    onSuccess: () => {
      setOptimisticFollowState(null);
      refetchFollowStatus().catch(console.error);
    },
    onError: () => {
      setOptimisticFollowState(null);
      onFollowChange?.(isFollowing); // Revert to original state
    },
  });

  const {
    followTrack,
    unfollowTrack,
    isPending: isTrackPending,
  } = useFollowTrack({
    onSuccess: () => {
      setOptimisticFollowState(null);
      refetchFollowStatus().catch(console.error);
    },
    onError: () => {
      setOptimisticFollowState(null);
      onFollowChange?.(isFollowing); // Revert to original state
    },
  });

  const handleClick = () => {
    // An anonymous share-link visitor cannot follow; we show a tooltip instead.
    // A signed-in visitor follows normally, share link or not.
    if (isAnonymousShare) {
      return;
    }

    const isPending = isArtistPending || isTrackPending;

    if (isPending) {
      return; // Prevent multiple clicks during loading
    }

    const newFollowState = !isFollowing;

    // Optimistically update the UI immediately
    setOptimisticFollowState(newFollowState);
    onFollowChange?.(newFollowState);

    // Call the appropriate mutation (callbacks handle success/error)
    if (isFollowing) {
      if (isArtist) {
        unfollowArtist(entityId);
      } else {
        unfollowTrack(entityId);
      }
    } else if (isArtist) {
      followArtist(entityId);
    } else {
      followTrack(entityId);
    }
  };

  const isPending = isArtistPending || isTrackPending;
  const isDisabled = isPending || isAnonymousShare;

  if (variant === 'icon') {
    return (
      <>
        <button
          id={tooltipTargetId}
          type="button"
          className={`${styles.iconButton} ${styles[`size-${size}`]} ${className}`}
          onClick={handleClick}
          disabled={isDisabled}
          title={isFollowing ? intl.formatMessage(messages.following) : intl.formatMessage(messages.follow)}
        >
          <FontAwesomeIcon
            icon={isFollowing ? faHeartSolid : faHeart}
            className={`${styles.icon} ${isFollowing ? styles.iconFollowing : styles.iconNotFollowing}`}
          />
          {isPending && <div className={styles.spinner} />}
        </button>
        {isAnonymousShare && (
          <UncontrolledTooltip target={tooltipTargetId} placement="top">
            {intl.formatMessage(messages.publicTooltip)}
          </UncontrolledTooltip>
        )}
      </>
    );
  }

  const getButtonText = () => {
    if (isPending) {
      return isFollowing ? 'Unfollowing...' : 'Following...';
    }

    if (isFollowing) {
      return isHovered ? intl.formatMessage(messages.unfollow) : intl.formatMessage(messages.following);
    }

    return intl.formatMessage(messages.follow);
  };

  return (
    <>
      <button
        id={tooltipTargetId}
        type="button"
        className={`${styles.button} ${styles[`size-${size}`]} ${
          isFollowing ? styles.buttonFollowing : styles.buttonNotFollowing
        } ${isPending ? styles.buttonPending : ''} ${className}`}
        onClick={handleClick}
        disabled={isDisabled}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <FontAwesomeIcon icon={isFollowing ? faHeartSolid : faHeart} className={styles.buttonIcon} />
        <span>{getButtonText()}</span>
        {isPending && <div className={styles.spinner} />}
      </button>
      {isAnonymousShare && (
        <UncontrolledTooltip target={tooltipTargetId} placement="top">
          {intl.formatMessage(messages.publicTooltip)}
        </UncontrolledTooltip>
      )}
    </>
  );
};

export const FollowButton = TypedMemo(FollowButtonComponent);
